import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import { WebSocketResponseMessage } from '../../proto/generated/websocket'
import { OrderStatus } from '../../proto/generated/order'
import { useSelector } from 'react-redux'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { useDispatch } from 'react-redux'
import { addOrderFiredAlert } from '../../reducer/orders.reducer'

const OrderStatusAlert = () => {
    const dispatch = useDispatch()
    const successSoundRef = useRef<HTMLAudioElement>(null)
    const errorSoundRef = useRef<HTMLAudioElement>(null)
    const ws = useContext(WebSocketContext)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)

    const onWebSocketMessageReceived = useCallback(async(data: Uint8Array) => {
        const responseData = WebSocketResponseMessage.decode(data)
        try {
            if (responseData.orderUpdate && responseData.orderUpdate.orders.length > 0 && responseData.orderUpdate.isPostback && primaryTradingAccount && responseData.orderUpdate.tradingAccountId === primaryTradingAccount.accountId) {
                const orderUpdates = responseData.orderUpdate.orders
                for (const order of orderUpdates) {
                    const secKey = getSecurityKey(order.security?.exchange??0, order.security?.code??'')
                    const security = await getSecurityAsync(secKey)
                    if (order.status === OrderStatus.EXECUTED) {
                        dispatch(addOrderFiredAlert({
                            ...order,
                            exchangeTimestamp: order.exchangeTimestamp || Date.now()
                            // message: `Order with security ${security?.displayName} executed successfully`
                        }))
                        if (successSoundRef.current) {
                            if (!successSoundRef.current.paused) {
                                successSoundRef.current.pause()
                                successSoundRef.current.currentTime = 0
                            }
                            if (errorSoundRef.current && !errorSoundRef.current.paused) {
                                errorSoundRef.current.pause()
                                errorSoundRef.current.currentTime = 0
                            }
                            successSoundRef.current.play()
                        }
                    } else if (order.status && [OrderStatus.FAILED, OrderStatus.REJECTED].includes(order.status)) {
                        dispatch(addOrderFiredAlert({
                            ...order,
                            exchangeTimestamp: order.exchangeTimestamp || Date.now()
                            // message: `Order with security ${security?.displayName} Failed / Rejected`
                        }))
                        if (errorSoundRef.current) {
                            if (successSoundRef.current && !successSoundRef.current.paused) {
                                successSoundRef.current.pause()
                                successSoundRef.current.currentTime = 0
                            }
                            if (!errorSoundRef.current.paused) {
                                errorSoundRef.current.pause()
                                errorSoundRef.current.currentTime = 0
                            }
                            errorSoundRef.current.play()
                        }
                    }
                }
            }
        } catch (e) {
            console.error(e, 'sound play error')
        }
    }, [primaryTradingAccount])

    useEffect(() => {
        ws!.addListener(onWebSocketMessageReceived)
        return () => {
            if (ws !== null) {
                ws.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    return (
        <div style={{
            position: 'fixed',
            bottom: '16px',
            left: '16px',
            display: 'flex',
            flexDirection: 'column-reverse',
            alignItems: 'flex-start',
            zIndex: 9999
        }}>
            <audio ref={successSoundRef} src={'/sound/success.mp3'} />
            <audio ref={errorSoundRef} src="/sound/error.mp3" />
        </div>
    )
}

export default memo(OrderStatusAlert)